import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { GlassCard } from '@/components/shared/redesign';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useTransactionPalette } from '@/hooks/admin/useTransactionPalette';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';
import { IAdminTransaction } from '@/types/admin/transactions';
import {
  getPaymentStatusColor,
  isPayment as checkIsPayment,
  getTransactionAmountColor,
  getTransactionTypeLabel,
  hasTransactionDetails,
} from '@/utils/admin/transactions';

interface TransactionCardProps {
  transaction: IAdminTransaction;
}

export const TransactionCard: React.FC<TransactionCardProps> = ({ transaction }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const styles = useStyles();
  const { admin, redesign, semantic } = useAdminColors();
  const transactionPalette = useTransactionPalette();

  const statusPalette = useMemo(
    () => ({
      success: semantic.status.success,
      error: semantic.status.error,
      pending: semantic.status.warning,
      voided: semantic.text.muted,
      fallback: redesign.text.dim,
    }),
    [
      semantic.status.success,
      semantic.status.error,
      semantic.status.warning,
      semantic.text.muted,
      redesign.text.dim,
    ],
  );

  const amountPalette = useMemo(
    () => ({ payment: transactionPalette.payment, charge: transactionPalette.charge }),
    [transactionPalette.payment, transactionPalette.charge],
  );

  const isPayment = checkIsPayment(transaction);
  const hasDetails = hasTransactionDetails(transaction);
  const amountColor = getTransactionAmountColor(transaction, amountPalette);
  const typeBadgeColor = isPayment ? transactionPalette.payment : transactionPalette.charge;

  const toggleExpand = () => setIsExpanded((v) => !v);

  return (
    <GlassCard
      padding={16}
      interactive={hasDetails}
      onPress={hasDetails ? toggleExpand : undefined}
    >
      {/* Header: date + type badge on left, amount on right */}
      <View style={styles.cardHeader}>
        <View style={styles.headerLeft}>
          <Text style={styles.date}>{formatShortDate(transaction.transactionDate)}</Text>
          <View style={[styles.typeBadge, { backgroundColor: typeBadgeColor + '20' }]}>
            <Text style={[styles.typeBadgeText, { color: redesign.text.primary }]}>
              {getTransactionTypeLabel(transaction)}
            </Text>
          </View>
        </View>
        <Text style={[styles.amount, { color: amountColor, fontWeight: '700' }]}>
          {formatCurrency(Math.abs(transaction.amount))}
        </Text>
      </View>

      {/* Description */}
      <Text style={styles.description} numberOfLines={isExpanded ? undefined : 2}>
        {transaction.description}
      </Text>

      {transaction.arCodeDescription && (
        <Text style={styles.arCode}>{transaction.arCodeDescription}</Text>
      )}

      {/* Expanded details */}
      {isExpanded && hasDetails && (
        <View style={styles.expandedSection}>
          {transaction.paidByFirstName && transaction.paidByLastName && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Paid By:</Text>
              <Text style={styles.detailValue}>
                {transaction.paidByFirstName} {transaction.paidByLastName}
              </Text>
            </View>
          )}

          {transaction.paymentSettlementStatus && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Status:</Text>
              <View
                style={[
                  styles.statusBadge,
                  {
                    backgroundColor:
                      getPaymentStatusColor(transaction.paymentSettlementStatus, statusPalette) +
                      '20',
                  },
                ]}
              >
                <Text
                  style={[
                    styles.statusText,
                    {
                      color: getPaymentStatusColor(
                        transaction.paymentSettlementStatus,
                        statusPalette,
                      ),
                    },
                  ]}
                >
                  {transaction.paymentSettlementStatus}
                </Text>
              </View>
            </View>
          )}

          {transaction.paymentConvenienceFee && transaction.paymentConvenienceFee > 0 && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Convenience Fee:</Text>
              <Text style={styles.detailValue}>
                {formatCurrency(transaction.paymentConvenienceFee)}
              </Text>
            </View>
          )}

          {transaction.postMonth && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Post Month:</Text>
              <Text style={styles.detailValue}>{formatShortDate(transaction.postMonth)}</Text>
            </View>
          )}

          {transaction.arCode && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>AR Code:</Text>
              <Text style={styles.detailValue}>{transaction.arCode}</Text>
            </View>
          )}
        </View>
      )}

      {hasDetails && (
        <View style={styles.expandIndicator}>
          <Text style={styles.expandIndicatorText}>
            {isExpanded ? 'Tap to collapse' : 'Tap for details'}
          </Text>
          {isExpanded ? (
            <SVG_COMPONENTS.CHEVRON_UP width={12} height={12} color={admin.dataCell.textMuted} />
          ) : (
            <SVG_COMPONENTS.CHEVRON_RIGHT
              width={12}
              height={12}
              color={admin.dataCell.textMuted}
            />
          )}
        </View>
      )}
    </GlassCard>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
      },
      headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flex: 1,
      },
      date: {
        fontSize: 13,
        fontWeight: '600',
        color: components.admin.dataCell.textMuted,
      },
      typeBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 4,
      },
      typeBadgeText: {
        fontSize: 10,
        fontWeight: '600',
      },
      amount: {
        fontSize: 18,
      },
      description: {
        fontSize: 14,
        color: colors.text.primary,
        marginBottom: 4,
      },
      arCode: {
        fontSize: 12,
        color: components.admin.dataCell.textMuted,
        marginBottom: 8,
      },
      expandedSection: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: components.admin.dataTable.border,
      },
      detailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 6,
      },
      detailLabel: {
        fontSize: 13,
        color: components.admin.dataCell.textMuted,
        fontWeight: '500',
      },
      detailValue: {
        fontSize: 13,
        color: colors.text.primary,
        fontWeight: '600',
      },
      statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
      },
      statusText: {
        fontSize: 11,
        fontWeight: '600',
      },
      expandIndicator: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 8,
        gap: 4,
      },
      expandIndicatorText: {
        fontSize: 11,
        color: components.admin.dataCell.textMuted,
      },
    }),
  );
